import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import NTable from "../../../../components/NTable";
import Drawer from "../../../../components/Drawer";
import Button from "../../../../components/Button";
import Form from "./Form";
import { useNewTable } from "../../../../components/NTable/hook";
import { useColumns } from "./useColumns";
import { useQualificationsApi } from "../api";
import { ProfileContext } from "../../context";
import { useDeleteModal } from "../../../Settings/helpers/useDeleteModal";
import { useVisible } from "../../../../utils/hooks/useVisible";

const useCustomDrawer = (getChildren) => {
    const [wrapperElement, setWrapperElement] = useState(null);

    useLayoutEffect(() => {
        const element = document.getElementById("licenses-drawer");
        setWrapperElement(element);
    }, [setWrapperElement]);

    if (wrapperElement === null) return null;

    return createPortal(getChildren(), wrapperElement);
};

const Licenses = () => {
    const { t } = useTranslation();
    const { isSelf, user } = useContext(ProfileContext);
    const { 
        getLicenses, 
        addLicense, 
        updateLicense, 
        upload, 
        remove,
        deleteLicense, 
        data 
    } = useQualificationsApi();

    const { open: openDrawer, close: closeDrawer, visible: isDrawerOpen, selected } = useVisible();

    const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
        message: t("do-you-want-to-delete-this-license"),
        onConfirm: (id) => {
            const url = isSelf ? `/profile/license/${id}` : `/users/${user?.id}/license/${id}`;
            deleteLicense(url, id);
        },
        permission: isSelf ? "profile.changeLicense" : "people.changeLicense",
    });

    const columns = useColumns({
        edit: openDrawer,
        remove: openDeleteModal,
    });

    const table = useNewTable({
        data,
        columns,
        totalItems: data?.length,
        enableRowSelection: false,
    });

    useEffect(() => {
        const controller = new AbortController();
        const url = isSelf ? "/profile/licenses" : `/users/${user?.id}/licenses`;
        if (user?.id) {
            getLicenses(url, controller);
        }
        return () => controller.abort();
    }, [getLicenses, isSelf, user]);

    const drawer = useCustomDrawer(() => {
        return (
            <Drawer
                title={selected?.id ? t("edit-license") : t("add-license")}
                open={isDrawerOpen}
                close={closeDrawer}
                style={{
                    maxWidth: "initial",
                }}
            >
                <Form
                    closeDrawer={closeDrawer}
                    selected={selected}
                    addLicense={addLicense}
                    updateLicense={updateLicense}
                    upload={upload}
                    remove={remove}
                />
            </Drawer>
        );
    });

    return (
        <>
            <div className="mt-4">
                <div className="d-flex justify-content-end mx-4 mb-3">
                    <Button
                        className="border-dark btn-round btn-icon shadow-none btn btn-none btn-sm h-25"
                        onClick={() => openDrawer()}
                    >
                        {t("add-license")}
                    </Button>
                </div>
                <NTable table={table} />
            </div>

            {drawer}

            {deleteModal}
        </>
    );
};

export default Licenses;
